import {
    CheckOutlined,
    CloseOutlined,
    DollarCircleOutlined
} from '@ant-design/icons';
import { Button, Modal, message } from 'antd';
import React, { useRef, useState } from 'react';
import { redirect, useNavigate, useRouteLoaderData, useSubmit } from 'react-router-dom';
import { recordOfflinePayment } from '../../api/finance/invoice.js';
import { Currency, DatePicker } from '../../components/inputs/index.js';
import { formatPrice, formatTodayBackendDate } from '../../library/utilities/intl.js';
import { CancelButton } from '../../ui';

const ManualPayment = () => {
    const { invoiceDetails, invoiceLines } = useRouteLoaderData('InvoiceDetails') || {};
    const navigate = useNavigate();
    const submit = useSubmit();
    const currency = invoiceDetails?.currency?.code || 'EUR';

    const calculatedLineTotal = (invoiceLines || []).reduce((sum, l) => sum + (Number(l.total_price_incl_vat) || 0), 0);
    const totalInvoiceAmount = invoiceDetails?.total_amount || (calculatedLineTotal > 0 ? calculatedLineTotal : (invoiceDetails?.total_amount_open ?? 0));
    const amountPaid = invoiceDetails?.amount_paid ?? invoiceDetails?.total_amount_paid ?? 0;
    const openAmount = invoiceDetails?.total_amount_open ?? invoiceDetails?.amount_open ?? Math.max(0, totalInvoiceAmount - amountPaid);

    const [paymentDate, setPaymentDate] = useState(formatTodayBackendDate());
    const [paymentValue, setPaymentValue] = useState(openAmount > 0 ? openAmount : null);
    const amount = useRef(openAmount > 0 ? openAmount : null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = () => {
        if (isSubmitting) return; // Prevent multiple submissions
        if (!paymentDate) {
            message.error('Payment date is required');
            return;
        }
        if (!amount.current || amount.current <= 0 || isNaN(amount.current)) {
            message.error('Valid amount is required');
            return;
        }
        setIsSubmitting(true);
        submit(
            { amount: amount.current, payment_date: paymentDate },
            {
                encType: 'application/json',
                method: 'post',
                replace: true
            }
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 600 }}>
                    <DollarCircleOutlined style={{ color: '#10b981', fontSize: '18px' }} />
                    <span>Record Offline Payment</span>
                </div>
            }
            open={true}
            onCancel={() => navigate('..', { replace: true })}
            width={460}
            centered
            forceRender
            destroyOnClose
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' }}>
                    <CancelButton
                        icon={<CloseOutlined />}
                        onClick={() => navigate('..', { replace: true })}
                    >
                        Cancel
                    </CancelButton>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                    >
                        Save Payment
                    </Button>
                </div>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
                {/* Invoice Summary Box */}
                <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                        <span style={{ color: '#64748b' }}>Invoice No.</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                            {invoiceDetails?.invoice_number || `#${invoiceDetails?.id || ''}`}
                        </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                        <span style={{ color: '#64748b' }}>Invoice Total</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                            {formatPrice(totalInvoiceAmount, currency)}
                        </span>
                    </div>

                    {amountPaid > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                            <span style={{ color: '#64748b' }}>Amount Paid</span>
                            <span style={{ fontWeight: 500, color: '#16a34a' }}>
                                {formatPrice(amountPaid, currency)}
                            </span>
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '8px',
                        marginTop: '2px',
                        borderTop: '1px dashed #cbd5e1',
                        fontSize: '14px'
                    }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>Outstanding Amount</span>
                        <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f766e' }}>
                            {formatPrice(openAmount, currency)}
                        </span>
                    </div>
                </div>

                {/* Payment Date Input */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                        Payment Date <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={{ width: '100%' }}>
                        <DatePicker
                            value={paymentDate}
                            disablePastDates={false}
                            onChange={(val) => {
                                setPaymentDate(val);
                            }}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                {/* Amount Input */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                            Amount Received <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        {openAmount > 0 && (
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', fontSize: '12px', color: '#0284c7' }}
                                onClick={() => {
                                    setPaymentValue(openAmount);
                                    amount.current = openAmount;
                                }}
                            >
                                Set full amount ({formatPrice(openAmount, currency)})
                            </Button>
                        )}
                    </div>
                    <div style={{ width: '100%' }}>
                        <Currency
                            currency={currency}
                            name="amount"
                            value={paymentValue}
                            onChange={(val) => {
                                setPaymentValue(val);
                                amount.current = val;
                            }}
                            style={{ width: '100%', maxWidth: '100%' }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

ManualPayment.Action = async ({ params, request }) => {
    const { invoiceId } = params;
    const data = await request.json();

    if (!data.amount) {
        message.error('Amount is required');
        return false;
    }

    if (data.amount <= 0 || isNaN(data.amount)) {
        message.error('Invalid amount');
        return false;
    }

    if (!data.payment_date) {
        message.error('Payment date is required');
        return false;
    }

    try {
        const msg = await recordOfflinePayment(invoiceId, data.amount, data.payment_date);
        message.success(msg);
        return redirect('..', {
            replace: true
        });
    }
    catch (error) {
        message.error(error.message);
    }
    return false;
};

export { ManualPayment };
